import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ScoresState } from "./types";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Hook qui gère la sauvegarde des résultats dans la base de données
 */
export const useResultSaver = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  /**
   * Sauvegarde le score d'une tentative individuelle dans la base de données
   */
  const saveIndividualScore = async (
    selectedTest: string | null,
    currentPoint: string,
    currentAttempt: number,
    score: number,
    tendency: any = null
  ) => {
    if (!selectedTest) return;
    
    try {
      // Get current path to determine level
      const currentPath = window.location.pathname;
      let level = "3"; // Default to level 3
      
      if (currentPath.includes("level1")) {
        level = "1";
      } else if (currentPath.includes("level2")) {
        level = "2";
      }
      
      // Check if user is authenticated
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        console.log("No authenticated session found");
        return; // Don't show toast to avoid disrupting user experience
      }
      
      // Prepare data for saving individual score
      const scoreEntry = {
        user_id: session.session.user.id,
        test_id: selectedTest,
        level: level,
        point: currentPoint,
        attempt: currentAttempt,
        score: score,
        tendency: tendency,
        created_at: new Date().toISOString()
      };
      
      console.log("Saving individual score entry:", scoreEntry);
      
      const { data, error } = await supabase
        .from('individual_scores')
        .insert(scoreEntry)
        .select();
      
      if (error) {
        console.error("Error saving individual score:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
      } else {
        console.log("Individual score saved successfully:", data);
        // Invalider les caches pour forcer le rafraîchissement des statistiques
        queryClient.invalidateQueries({ queryKey: ['distanceStats'] });
        queryClient.invalidateQueries({ queryKey: ['temporalDistanceStats'] });
        queryClient.invalidateQueries({ queryKey: ['training-results'] });
      }
    } catch (err) {
      console.error("Exception while saving individual score:", err);
    }
  };
  
  /**
   * Sauvegarde les résultats complets d'un test dans la base de données
   */
  const saveTestResults = async (
    selectedTest: string | null,
    totalPoints: number
  ) => {
    if (!selectedTest) return false;
    
    // Get current path to determine level
    const currentPath = window.location.pathname;
    let level = "3"; // Default to level 3
    
    if (currentPath.includes("level1")) {
      level = "1";
    } else if (currentPath.includes("level2")) {
      level = "2";
    }

    console.log(`Saving test results for test: ${selectedTest}, level: ${level}, path: ${currentPath}`);
    console.log(`Total points: ${totalPoints}`);
    
    try {
      // Check if user is authenticated
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        console.log("No authenticated session found");
        toast({
          variant: "destructive",
          title: "Erreur d'authentification",
          description: "Vous devez être connecté pour sauvegarder vos résultats.",
        });
        navigate("/auth");
        return false;
      }
      
      // Prepare data for saving
      const result = {
        user_id: session.session.user.id,
        test_id: selectedTest,
        level: level,
        points: totalPoints,
        index: 0, // This could be calculated based on points if needed
      };
      
      console.log("Saving test result to database:", result);
      
      // Save to level_tests_results table
      const { data, error } = await supabase
        .from('level_tests_results')
        .insert([result]);
      
      if (error) {
        console.error("Error saving test result:", error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Une erreur est survenue lors de la sauvegarde des résultats: " + error.message,
        });
        return false;
      }
      
      console.log("Test result saved successfully:", data);
      
      // Invalider le cache des résultats d'entraînement
      queryClient.invalidateQueries({ queryKey: ['training-results'] });
      queryClient.invalidateQueries({ queryKey: ['distanceStats'] });
      queryClient.invalidateQueries({ queryKey: ['temporalDistanceStats'] });
      
      toast({
        title: "Résultats sauvegardés",
        description: `Votre score de ${totalPoints} points a été enregistré pour le test ${selectedTest}.`,
      });
      
      return true;
    } catch (err) {
      console.error("Exception while saving test result:", err);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une exception est survenue lors de la sauvegarde des résultats.",
      });
      return false;
    }
  };
  
  return {
    saveIndividualScore,
    saveTestResults
  };
};
